import { useMemo, useState } from 'react'
import type { DragEvent, ReactElement } from 'react'
import type { Locale } from '@siteshowroom/shared'
import { useLocale, useTranslate } from '../lib/i18n-provider'
import { MEDIA_CATEGORIES, mediaFileUrl } from '../lib/media-client'
import type { MediaAsset, MediaCategory, MediaText } from '../lib/media-client'
import { spaceLabel } from '../lib/spaces-client'
import type { Space } from '../lib/spaces-client'

/**
 * Het raster met de foto's van één residence.
 *
 * Wat hier gebeurt is geen presentatie maar redactie: volgorde, hoofdfoto,
 * categorie, alt-tekst en bijschrift zijn **data** die de kanalen overnemen
 * (§17.14, CLAUDE.md regel 10). Daarom staat elk van die vier op de tegel zelf
 * en niet weggestopt achter een detailscherm.
 *
 * Over de volgorde wijzigen: slepen met de HTML5 drag-and-drop-API is prettig met
 * een muis en volstrekt onbedienbaar zonder. Naast de sleepgreep staan daarom
 * knoppen voor omhoog en omlaag — geen extra functie maar dezelfde functie, en de
 * enige die met toetsenbord of schermlezer werkt.
 */

export interface MediaGridProps {
  readonly assets: readonly MediaAsset[]
  /** De ruimtes van deze woning; leeg wanneer de verhuurder er nog geen heeft. */
  readonly spaces: readonly Space[]
  /** De talen waarvoor alt-tekst en bijschrift ingevuld kunnen worden. */
  readonly locales: readonly Locale[]
  /** Zet de knoppen uit zolang een wijziging onderweg is. */
  readonly busy: boolean
  readonly onSetPrimary: (assetId: string) => void
  readonly onChangeCategory: (assetId: string, category: MediaCategory) => void
  /** `null` haalt de foto uit zijn ruimte. */
  readonly onAssignSpace: (assetId: string, spaceId: string | null) => void
  readonly onSaveText: (assetId: string, locale: Locale, text: MediaText) => void
  /** De volledige nieuwe volgorde; zie `reorderMedia` in de client. */
  readonly onReorder: (assetIds: readonly string[]) => void
  readonly onDelete: (assetId: string) => void
}

const EMPTY_TEXT: MediaText = { altText: '', caption: '' }

/**
 * Verplaatst één id binnen de lijst. Pure functie, los van React: dit is de enige
 * plek waar de nieuwe volgorde ontstaat, of die nu van een sleepactie of van een
 * knop komt.
 */
export function moveInOrder(ids: readonly string[], from: number, to: number): readonly string[] {
  if (from === to || from < 0 || to < 0 || from >= ids.length || to >= ids.length) {
    return ids
  }
  const next = [...ids]
  const [moved] = next.splice(from, 1)
  if (moved === undefined) {
    return ids
  }
  next.splice(to, 0, moved)
  return next
}

/**
 * De volgorde zoals de verhuurder hem bedoeld heeft: `sortOrder` is leidend, en
 * bij gelijke waarden beslist het moment van uploaden. Bewust niet de hoofdfoto
 * vooraan zetten — dat doet de publieke API voor de gastensite, maar in het
 * beheerscherm zou een tegel dan verspringen zodra je hem tot hoofdfoto maakt.
 */
function byOrder(left: MediaAsset, right: MediaAsset): number {
  if (left.sortOrder !== right.sortOrder) {
    return left.sortOrder - right.sortOrder
  }
  return left.createdAt.localeCompare(right.createdAt)
}

interface MediaCardProps {
  readonly asset: MediaAsset
  readonly position: number
  readonly total: number
  readonly spaces: readonly Space[]
  readonly locales: readonly Locale[]
  readonly busy: boolean
  readonly onSetPrimary: (assetId: string) => void
  readonly onChangeCategory: (assetId: string, category: MediaCategory) => void
  readonly onAssignSpace: (assetId: string, spaceId: string | null) => void
  readonly onSaveText: (assetId: string, locale: Locale, text: MediaText) => void
  readonly onMove: (from: number, to: number) => void
  readonly onDelete: (assetId: string) => void
  readonly onDragStart: (assetId: string) => void
  readonly onDropOn: (assetId: string) => void
}

function MediaCard({
  asset,
  position,
  total,
  spaces,
  locales,
  busy,
  onSetPrimary,
  onChangeCategory,
  onAssignSpace,
  onSaveText,
  onMove,
  onDelete,
  onDragStart,
  onDropOn,
}: MediaCardProps): ReactElement {
  const t = useTranslate()
  const { locale: uiLocale } = useLocale()

  /**
   * De invoervelden houden hun eigen concept vast en sturen pas bij het verlaten
   * van het veld. Bij iedere toetsaanslag opslaan levert een aanvraag per letter
   * op; een knop per taal erbij maakt de tegel onleesbaar.
   */
  const [drafts, setDrafts] = useState<Readonly<Record<string, MediaText>>>(() => {
    const initial: Record<string, MediaText> = {}
    for (const locale of locales) {
      initial[locale] = asset.texts[locale] ?? EMPTY_TEXT
    }
    return initial
  })

  const languageNames = useMemo(
    () => new Intl.DisplayNames([uiLocale], { type: 'language' }),
    [uiLocale],
  )

  function draftFor(locale: Locale): MediaText {
    return drafts[locale] ?? EMPTY_TEXT
  }

  function updateDraft(locale: Locale, patch: Partial<MediaText>): void {
    setDrafts((current) => ({
      ...current,
      [locale]: { ...(current[locale] ?? EMPTY_TEXT), ...patch },
    }))
  }

  /** Alleen sturen wat werkelijk veranderd is; anders is elke blur een aanvraag. */
  function commitDraft(locale: Locale): void {
    const draft = draftFor(locale)
    const stored = asset.texts[locale] ?? EMPTY_TEXT
    if (draft.altText !== stored.altText || draft.caption !== stored.caption) {
      onSaveText(asset.id, locale, draft)
    }
  }

  function handleDragStart(event: DragEvent<HTMLElement>): void {
    // Zonder payload weigert Firefox de sleepactie te starten.
    event.dataTransfer.setData('text/plain', asset.id)
    event.dataTransfer.effectAllowed = 'move'
    onDragStart(asset.id)
  }

  return (
    <li
      className={asset.isPrimary ? 'media-card media-card--primary' : 'media-card'}
      onDragOver={(event) => {
        // Zonder dit accepteert de browser de drop niet.
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
      }}
      onDrop={(event) => {
        event.preventDefault()
        onDropOn(asset.id)
      }}
    >
      <div className="media-card__figure">
        {/*
          Lege alt: de bestandsnaam staat er in tekst naast, dus een tweede keer
          hetzelfde uitspreken helpt een schermlezer niet. De alt-tekst die er wél
          toe doet is de ingevoerde waarde, en die is hier het onderwerp van de
          bewerking — geen bijschrift bij een voorbeeldweergave.
        */}
        <img
          alt=""
          className="media-card__thumb"
          height={180}
          loading="lazy"
          src={mediaFileUrl(asset.urls.thumb)}
          // De dominante kleur staat eronder zolang de afbeelding laadt.
          style={{
            backgroundColor: asset.dominantColor === '' ? undefined : asset.dominantColor,
          }}
          width={320}
        />
        {asset.isPrimary && (
          <span className="media-card__badge">{t('dashboard.media.primaryBadge')}</span>
        )}
        {asset.status !== 'ready' && (
          <span className="media-card__status" data-status={asset.status}>
            {t(`dashboard.media.status.${asset.status}`)}
          </span>
        )}
      </div>

      <p className="media-card__name" title={asset.originalFilename}>
        {/* Data uit het bestandssysteem; bewust onvertaald. */}
        {asset.originalFilename}
      </p>
      <p className="media-card__meta">
        {t('dashboard.media.dimensions', { width: asset.width, height: asset.height })}
      </p>

      <div className="media-card__order">
        {/* De greep is het enige sleepbare deel: een sleepbare tegel maakt de
            invoervelden erin onbruikbaar in sommige browsers. */}
        <span
          aria-hidden="true"
          className="media-card__handle"
          draggable
          onDragStart={handleDragStart}
        >
          ⠿
        </span>
        <span className="media-card__position">
          {t('dashboard.media.position', { position, total })}
        </span>
        <button
          aria-label={t('dashboard.media.moveUp')}
          className="button button--icon"
          disabled={busy || position <= 1}
          onClick={() => {
            onMove(position - 1, position - 2)
          }}
          type="button"
        >
          ↑
        </button>
        <button
          aria-label={t('dashboard.media.moveDown')}
          className="button button--icon"
          disabled={busy || position >= total}
          onClick={() => {
            onMove(position - 1, position)
          }}
          type="button"
        >
          ↓
        </button>
      </div>

      {/* De ruimtekeuze verschijnt pas wanneer er ruimtes zijn. Een lege lijst met
          alleen "geen ruimte" erin is een vraag zonder antwoordmogelijkheid. */}
      {spaces.length > 0 && (
        <label className="field">
          <span className="field__label">{t('dashboard.media.space.label')}</span>
          <select
            className="field__input"
            disabled={busy}
            onChange={(event) => {
              onAssignSpace(asset.id, event.target.value === '' ? null : event.target.value)
            }}
            value={asset.spaceId ?? ''}
          >
            <option value="">{t('dashboard.media.space.none')}</option>
            {spaces.map((space) => (
              <option key={space.id} value={space.id}>
                {spaceLabel(space, spaces, t)}
              </option>
            ))}
          </select>
        </label>
      )}

      <label className="field">
        <span className="field__label">{t('dashboard.media.category.label')}</span>
        <select
          className="field__input"
          /* Hoort de foto bij een ruimte, dan volgt de categorie daaruit en is hij
             hier niet los te kiezen. Anders kan een foto in de keuken staan met
             categorie 'bedroom', en dan moet een kanaal raden welke van de twee
             klopt (§17.14). */
          disabled={busy || asset.spaceId !== null}
          onChange={(event) => {
            const next = MEDIA_CATEGORIES.find((candidate) => candidate === event.target.value)
            if (next !== undefined) {
              onChangeCategory(asset.id, next)
            }
          }}
          value={asset.category}
        >
          {MEDIA_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {t(`dashboard.media.category.${category}`)}
            </option>
          ))}
        </select>
        {asset.spaceId !== null && (
          <span className="field__hint">{t('dashboard.media.category.fromSpace')}</span>
        )}
      </label>

      {locales.map((locale) => (
        <fieldset className="media-card__texts" key={locale}>
          <legend className="media-card__legend">{languageNames.of(locale) ?? locale}</legend>
          <label className="field">
            <span className="field__label">{t('dashboard.media.altText')}</span>
            <input
              className="field__input"
              onBlur={() => {
                commitDraft(locale)
              }}
              onChange={(event) => {
                updateDraft(locale, { altText: event.target.value })
              }}
              type="text"
              value={draftFor(locale).altText}
            />
          </label>
          <label className="field">
            <span className="field__label">{t('dashboard.media.caption')}</span>
            <input
              className="field__input"
              onBlur={() => {
                commitDraft(locale)
              }}
              onChange={(event) => {
                updateDraft(locale, { caption: event.target.value })
              }}
              type="text"
              value={draftFor(locale).caption}
            />
          </label>
        </fieldset>
      ))}

      <div className="media-card__actions">
        {!asset.isPrimary && (
          <button
            className="button"
            disabled={busy}
            onClick={() => {
              onSetPrimary(asset.id)
            }}
            type="button"
          >
            {t('dashboard.media.makePrimary')}
          </button>
        )}
        <button
          className="button button--danger"
          disabled={busy}
          onClick={() => {
            // Verwijderen is onomkeerbaar voor de bezoeker van dit scherm: het
            // bestand verdwijnt uit de opslag. Eén bevestiging is hier terecht.
            if (window.confirm(t('dashboard.media.confirmDelete'))) {
              onDelete(asset.id)
            }
          }}
          type="button"
        >
          {t('dashboard.media.delete')}
        </button>
      </div>
    </li>
  )
}

export function MediaGrid({
  assets,
  spaces,
  locales,
  busy,
  onSetPrimary,
  onChangeCategory,
  onAssignSpace,
  onSaveText,
  onReorder,
  onDelete,
}: MediaGridProps): ReactElement {
  const t = useTranslate()
  const [draggedId, setDraggedId] = useState<string | null>(null)

  const ordered = useMemo(() => [...assets].sort(byOrder), [assets])
  const orderedIds = useMemo(() => ordered.map((asset) => asset.id), [ordered])

  function move(from: number, to: number): void {
    const next = moveInOrder(orderedIds, from, to)
    if (next !== orderedIds) {
      onReorder(next)
    }
  }

  function handleDropOn(targetId: string): void {
    if (draggedId === null || draggedId === targetId) {
      setDraggedId(null)
      return
    }
    move(orderedIds.indexOf(draggedId), orderedIds.indexOf(targetId))
    setDraggedId(null)
  }

  if (ordered.length === 0) {
    return (
      <p className="page__status" role="status">
        {t('common.state.empty')}
      </p>
    )
  }

  return (
    <ul className="media-grid">
      {ordered.map((asset, index) => (
        <MediaCard
          asset={asset}
          busy={busy}
          key={asset.id}
          locales={locales}
          onAssignSpace={onAssignSpace}
          onChangeCategory={onChangeCategory}
          onDelete={onDelete}
          onDragStart={setDraggedId}
          onDropOn={handleDropOn}
          onMove={move}
          onSaveText={onSaveText}
          onSetPrimary={onSetPrimary}
          position={index + 1}
          spaces={spaces}
          total={ordered.length}
        />
      ))}
    </ul>
  )
}
